import { supabase } from '@/integrations/supabase/client';
import { getCurrentEmpresaId } from '@/integrations/supabase/auth-utils';

export interface FinancialTotals {
  totalReceber: number;
  totalPagar: number;
  saldo: number;
}

/**
 * Calcula os totais financeiros combinando contas manuais e dados de pedidos
 * Garante consistência entre Fluxo de Caixa e Controle Financeiro
 */
export async function getTotaisFinanceiros(range?: {
  startDate?: Date | string;
  endDate?: Date | string;
}): Promise<FinancialTotals> {
  try {
    const empresaId = await getCurrentEmpresaId();
    if (!empresaId) {
      return { totalReceber: 0, totalPagar: 0, saldo: 0 };
    }

    const toDateString = (value?: Date | string) => {
      if (!value) return undefined;
      if (value instanceof Date) return value.toISOString().split('T')[0];
      return value;
    };

    const startDate = toDateString(range?.startDate);
    const endDate = toDateString(range?.endDate);

    // 1. Buscar contas a receber manuais (Fluxo de Caixa)
    // Nota: Não filtramos por data_vencimento aqui porque contas atrasadas têm vencimento no passado
    // O filtro de período é feito no Frontend baseado no status e datas
    const contasReceberQuery = supabase
      .from('contas_receber')
      .select('*')
      .eq('empresa_id', empresaId)
      .neq('status', 'cancelado'); // Excluir canceladas

    const { data: contasReceber, error: errorReceber } = await contasReceberQuery;

    // 2. Buscar contas a pagar manuais (Fluxo de Caixa)
    // Nota: Não filtramos por data_vencimento aqui porque contas atrasadas têm vencimento no passado
    // O filtro de período é feito no Frontend baseado no status e datas
    const contasPagarQuery = supabase
      .from('contas_pagar')
      .select('*')
      .eq('empresa_id', empresaId)
      .neq('status', 'cancelado'); // Excluir canceladas

    const { data: contasPagar, error: errorPagar } = await contasPagarQuery;

    // 3. Buscar pedidos e receitas (Controle Financeiro)
    let ordersQuery = supabase
      .from('atelie_orders')
      .select(`
        id,
        code,
        value,
        paid,
        status,
        created_at,
        delivery_date,
        customer_name,
        discount,
        discount_type
      `)
      .eq('empresa_id', empresaId)
      .neq('status', 'Cancelado'); // Excluir cancelados

    if (startDate && endDate) {
      ordersQuery = ordersQuery
        .gte('created_at', `${startDate}T00:00:00`)
        .lte('created_at', `${endDate}T23:59:59`);
    }

    const { data: orders, error: errorOrders } = await ordersQuery;

    let receitasQuery = supabase
      .from('atelie_receitas')
      .select('*')
      .eq('empresa_id', empresaId);

    if (startDate && endDate) {
      receitasQuery = receitasQuery
        .gte('created_at', `${startDate}T00:00:00`)
        .lte('created_at', `${endDate}T23:59:59`);
    }

    const { data: receitas, error: errorReceitas } = await receitasQuery;

    if (errorReceber) console.warn('[FINANCEIRO] Erro ao buscar contas_receber:', errorReceber);
    if (errorPagar) console.warn('[FINANCEIRO] Erro ao buscar contas_pagar:', errorPagar);
    if (errorOrders) console.warn('[FINANCEIRO] Erro ao buscar orders:', errorOrders);
    if (errorReceitas) console.warn('[FINANCEIRO] Erro ao buscar receitas:', errorReceitas);

  // 4. Calcular totais a receber
    let totalReceber = 0;

    // 4a. Somar contas a receber manuais (apenas pendentes)
    const contasReceberOrderIds = new Set<string>();
    if (contasReceber) {
      const contasReceberPendentes = contasReceber.filter(conta =>
        conta.status === 'pendente' || conta.status === 'atrasado'
      );

      contasReceber.forEach(conta => {
        if (conta.pedido_id) {
          contasReceberOrderIds.add(String(conta.pedido_id));
        }
      });

      totalReceber += contasReceberPendentes.reduce((acc, conta) => {
        return acc + (conta.valor_total - (conta.valor_recebido || 0));
      }, 0);

      console.log(`[FINANCEIRO] Contas receber manuais pendentes: ${contasReceberPendentes.length}, total: R$ ${totalReceber.toFixed(2)}`);
    }

    // 4b. Somar valores pendentes de pedidos (Controle Financeiro)
    if (orders) {
      orders.forEach(order => {
        if (contasReceberOrderIds.has(String(order.id))) {
          return;
        }
        const subtotal = Number(order.value || 0);
        const discount = Number((order as any)?.discount || 0);
        const discountType = (order as any)?.discount_type || 'fixed';
        const discountAmount = discountType === 'percentage'
          ? (subtotal * discount / 100)
          : discount;
        const totalValue = Math.max(0, subtotal - discountAmount);
        const receita = receitas?.find(r => r.order_code === order.code);
        const paidAmount = receita ? Number(receita.amount) : Number(order.paid || 0);
        const remainingAmount = totalValue - paidAmount;

        if (remainingAmount > 0) {
          totalReceber += remainingAmount;
        }
      });

      const pedidosComPendencia = orders.filter(order => {
        if (contasReceberOrderIds.has(String(order.id))) {
          return false;
        }
        const subtotal = Number(order.value || 0);
        const discount = Number((order as any)?.discount || 0);
        const discountType = (order as any)?.discount_type || 'fixed';
        const discountAmount = discountType === 'percentage'
          ? (subtotal * discount / 100)
          : discount;
        const totalValue = Math.max(0, subtotal - discountAmount);
        const receita = receitas?.find(r => r.order_code === order.code);
        const paidAmount = receita ? Number(receita.amount) : Number(order.paid || 0);
        return totalValue - paidAmount > 0;
      });

      console.log(`[FINANCEIRO] Pedidos com pendências: ${pedidosComPendencia.length}, adicionando ao total receber`);
    }

    // 5. Calcular totais a pagar (apenas contas manuais, pois pedidos não geram contas a pagar diretas)
    let totalPagar = 0;

    if (contasPagar) {
      const contasPagarPendentes = contasPagar.filter(conta =>
        conta.status === 'pendente' || conta.status === 'atrasado'
      );

      totalPagar = contasPagarPendentes.reduce((acc, conta) => {
        return acc + (conta.valor_total - (conta.valor_pago || 0));
      }, 0);

      console.log(`[FINANCEIRO] Contas pagar pendentes: ${contasPagarPendentes.length}, total: R$ ${totalPagar.toFixed(2)}`);
    }

    const saldo = totalReceber - totalPagar;

    console.log(`[FINANCEIRO] Totais calculados - Receber: R$ ${totalReceber.toFixed(2)}, Pagar: R$ ${totalPagar.toFixed(2)}, Saldo: R$ ${saldo.toFixed(2)}`);

    return {
      totalReceber,
      totalPagar,
      saldo
    };

  } catch (error) {
    console.error('[FINANCEIRO] Erro ao calcular totais financeiros:', error);
    return { totalReceber: 0, totalPagar: 0, saldo: 0 };
  }
}

export interface SaldoAtual {
  saldoInicial: number;
  dataSaldoInicial: string;
  receitasRealizadas: number;
  despesasRealizadas: number;
  saldoAtual: number;
}

export function calcularSaldoAtual(
  saldoInicial: number,
  contasPagar: any[] = [],
  contasReceber: any[] = []
): SaldoAtual {
  const receitasRealizadas = contasReceber
    .filter(conta => conta.status === 'recebido')
    .reduce((acc, conta) => acc + (conta.valor_recebido || 0), 0);

  const despesasRealizadas = contasPagar
    .filter(conta => conta.status === 'pago')
    .reduce((acc, conta) => acc + (conta.valor_pago || 0), 0);

  const saldoAtual = saldoInicial + receitasRealizadas - despesasRealizadas;

  return {
    saldoInicial,
    dataSaldoInicial: new Date().toISOString().split('T')[0],
    receitasRealizadas,
    despesasRealizadas,
    saldoAtual,
  };
}

/**
 * Calcula o saldo real atual da empresa incluindo todas as receitas e despesas
 * Considera:
 * - Saldo Inicial configurado
 * + Contas a receber recebidas (manuais)
 * + TODAS as vendas pagas (atelie_orders com paid>0, inclui pedidos normais + PDV)
 * - Contas a pagar pagas
 *
 * Esta é a FONTE OFICIAL de verdade para saldo em tempo real
 */
export async function calcularSaldoRealAtualAsync(): Promise<SaldoAtual> {
  try {
    const empresaId = await getCurrentEmpresaId();
    if (!empresaId) {
      return {
        saldoInicial: 0,
        dataSaldoInicial: new Date().toISOString().split('T')[0],
        receitasRealizadas: 0,
        despesasRealizadas: 0,
        saldoAtual: 0,
      };
    }

    // 1. Buscar saldo inicial
    const { data: configData } = await supabase
      .from('empresa_config')
      .select('saldo_inicial, data_saldo_inicial')
      .eq('empresa_id', empresaId)
      .maybeSingle();

    const saldoInicial = configData?.saldo_inicial || 0;

    // 2. Buscar contas a receber recebidas (manuais)
    const { data: contasReceber } = await supabase
      .from('contas_receber')
      .select('valor_recebido')
      .eq('empresa_id', empresaId)
      .eq('status', 'recebido');

    const receitasManuais = (contasReceber || []).reduce(
      (acc, conta) => acc + (Number(conta.valor_recebido) || 0),
      0
    );

    // 3. Buscar TODOS os pedidos pagos (atelie_orders com paid>0, não apenas PDV)
    const { data: vendas } = await supabase
      .from('atelie_orders')
      .select('value, discount, discount_type, type')
      .eq('empresa_id', empresaId)
      .gt('paid', 0)
      .neq('status', 'Cancelado');

    const receitasVendas = (vendas || []).reduce((acc, venda) => {
      const subtotal = Number(venda.value || 0);
      const discount = Number(venda.discount || 0);
      const discountType = venda.discount_type || 'fixed';
      const discountAmount = discountType === 'percentage'
        ? (subtotal * discount / 100)
        : discount;
      const totalValue = Math.max(0, subtotal - discountAmount);
      return acc + totalValue;
    }, 0);

    const receitasRealizadas = receitasManuais + receitasVendas;

    // 4. Buscar contas a pagar pagas
    const { data: contasPagar } = await supabase
      .from('contas_pagar')
      .select('valor_pago')
      .eq('empresa_id', empresaId)
      .eq('status', 'pago');

    const despesasRealizadas = (contasPagar || []).reduce(
      (acc, conta) => acc + (Number(conta.valor_pago) || 0),
      0
    );

    // 5. Calcular saldo real
    const saldoAtual = saldoInicial + receitasRealizadas - despesasRealizadas;

    return {
      saldoInicial,
      dataSaldoInicial: configData?.data_saldo_inicial || new Date().toISOString().split('T')[0],
      receitasRealizadas,
      despesasRealizadas,
      saldoAtual,
    };
  } catch (error) {
    console.error('[FINANCEIRO] Erro ao calcular saldo real atual:', error);
    return {
      saldoInicial: 0,
      dataSaldoInicial: new Date().toISOString().split('T')[0],
      receitasRealizadas: 0,
      despesasRealizadas: 0,
      saldoAtual: 0,
    };
  }
}

export interface MesComparativo {
  mes: string;
  mesNum: number;
  anoNum: number;
  receitas: number;
  despesas: number;
  saldo: number;
}

export function agregarPorMes(
  contasPagar: any[] = [],
  contasReceber: any[] = [],
  meses: number = 12,
  orders: any[] = [],
  receitas: any[] = []
): MesComparativo[] {
  console.log('[AGREGAR_POR_MES] Entrada:', { contasPagarCount: contasPagar.length, contasReceberCount: contasReceber.length, ordersCount: orders.length, receitasCount: receitas.length });

  const agora = new Date();
  const mesAtual = agora.getMonth();
  const anoAtual = agora.getFullYear();

  const dadosPorMes: Record<string, { receitas: number; despesas: number }> = {};

  for (let i = 0; i < meses; i++) {
    let mes = mesAtual - i;
    let ano = anoAtual;

    if (mes < 0) {
      mes = 12 + mes;
      ano -= 1;
    }

    const chave = `${ano}-${String(mes + 1).padStart(2, '0')}`;
    dadosPorMes[chave] = { receitas: 0, despesas: 0 };
  }

  // Agregar contas a receber manuais
  contasReceber.forEach(conta => {
    if (conta.status === 'recebido' && conta.data_recebimento) {
      const data = new Date(conta.data_recebimento);
      const chave = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;

      if (dadosPorMes[chave]) {
        dadosPorMes[chave].receitas += conta.valor_recebido || 0;
      }
    }
  });

  // Agregar contas a pagar manuais
  contasPagar.forEach(conta => {
    if (conta.status === 'pago' && conta.data_pagamento) {
      const data = new Date(conta.data_pagamento);
      const chave = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;

      if (dadosPorMes[chave]) {
        dadosPorMes[chave].despesas += conta.valor_pago || 0;
      }
    }
  });

  // Agregar receitas de pedidos (PDV)
  receitas.forEach(receita => {
    if (receita.payment_date) {
      const data = new Date(receita.payment_date);
      const chave = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;

      if (dadosPorMes[chave]) {
        dadosPorMes[chave].receitas += receita.amount || 0;
      }
    }
  });

  // Agregar pedidos normais (receitas quando o pedido foi pago) - APENAS SE NÃO JÁ ESTÁ EM ATELIE_RECEITAS
  orders.forEach(order => {
    if (order.paid && order.paid > 0 && order.created_at) {
      // Verificar se já existe receita correspondente em atelie_receitas
      const jaTemReceita = receitas.some(r => r.order_code === order.code);

      // Apenas agregar se NÃO tem receita (para evitar duplicação)
      if (!jaTemReceita) {
        const data = new Date(order.created_at);
        const chave = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;

        if (dadosPorMes[chave]) {
          const subtotal = Number(order.value || 0);
          const discount = Number(order.discount || 0);
          const discountType = order.discount_type || 'fixed';
          const discountAmount = discountType === 'percentage'
            ? (subtotal * discount / 100)
            : discount;
          const totalValue = Math.max(0, subtotal - discountAmount);

          dadosPorMes[chave].receitas += totalValue;
        }
      }
    }
  });

  const resultado: MesComparativo[] = Object.entries(dadosPorMes)
    .map(([chave, dados]) => {
      const [anoStr, mesStr] = chave.split('-');
      const ano = parseInt(anoStr);
      const mes = parseInt(mesStr);
      const data = new Date(ano, mes - 1);
      const mesNome = data.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });

      return {
        mes: mesNome,
        mesNum: mes,
        anoNum: ano,
        receitas: Number(dados.receitas) || 0,
        despesas: Number(dados.despesas) || 0,
        saldo: (Number(dados.receitas) || 0) - (Number(dados.despesas) || 0),
      };
    })
    .sort((a, b) => {
      if (a.anoNum !== b.anoNum) {
        return a.anoNum - b.anoNum;
      }
      return a.mesNum - b.mesNum;
    });

  console.log('[AGREGAR_POR_MES] Resultado:', resultado.map(r => `${r.mes}: R$ ${r.receitas.toFixed(2)} recv, R$ ${r.despesas.toFixed(2)} desp`).join(', '));

  return resultado;
}